import { T, ils, ils0, tnum, pct2 } from '../../../../theme/tokens';

/* Short money for an axis: 1.45M / 940K / 120. An axis exists to give the eye a
   scale, and "₪1,450,179" on four gridlines is four numbers nobody reads. */
const axisMoney = (v: number): string =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M`
  : v >= 1_000 ? `${Math.round(v / 1_000)}K`
  : `${Math.round(v)}`;

/* The value scale down the side of a chart. The brief asks for clean axes; three
   charts had gridlines and no numbers on them, which is a grid, not an axis. */
function ValueAxis({ max, height, steps = 4 }: { max: number; height: number; steps?: number }) {
  return (
    <div aria-hidden style={{
      position: 'absolute', insetInlineEnd: '100%', top: 0, height,
      paddingInlineEnd: 6, width: 46,
    }}>
      {Array.from({ length: steps }, (_, i) => {
        const frac = (steps - i) / steps;
        return (
          <div key={i} style={{
            position: 'absolute', top: height - frac * height - 6, insetInlineEnd: 6,
            fontSize: 9.5, color: T.inkFaint, fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}>
            {axisMoney(max * frac)}
          </div>
        );
      })}
    </div>
  );
}

/* A legend swatch and its label. Every chart on this screen carries one — the
   series are distinguished by colour, and a colour without a name is a riddle. */
export function Legend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12 }}>
      {items.map((it) => (
        <span key={it.label} style={{ fontSize: 11.5, color: T.inkSoft, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i aria-hidden style={it.dashed
            ? { display: 'inline-block', width: 14, height: 0, borderTop: `2px dashed ${it.color}` }
            : { display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── where the month lands: one bar, three certainties ───────────────────────
   The forecast is a sum of three very different things, and a single number hides
   which is which: money already in (certain), meetings already in the diary
   (likely — discounted by the realisation rate), and meetings not yet booked that
   the last three months say will be (a pattern, nothing more). One bar, three
   shades, the lightest one striped, so the eye sees at once how much of the
   forecast is fact and how much is habit. The previous whole month is a thin
   marker above the bar — the question the tile is asked is "do we get there".

   This replaced a 30-day line chart that, on the 2nd of the month, drew two days
   of data and a straight dashed guess to the 30th (Ohad, 02/11: "לא ברור מה
   רואים שם"). Nothing in the data says on which day the rest falls, so the
   calendar axis was carrying no information. */
export function ForecastBar({ revenue, booked, forecast, reference }: {
  revenue: number; booked: number; forecast: number;
  reference?: { label: string; value: number };
}) {
  const top = Math.max(forecast, reference?.value ?? 0, 1) * 1.04;
  const w = (v: number) => `${Math.max((v / top) * 100, 0)}%`;
  const segs = [
    { label: 'בוצע', v: revenue, bg: T.blue },
    { label: 'קבוע ביומן', v: Math.max(booked - revenue, 0), bg: '#93B4F7' },
    { label: 'עוד לא נקבע, צפוי לפי שלושת החודשים האחרונים', v: Math.max(forecast - booked, 0),
      bg: `repeating-linear-gradient(135deg, #DCE7FB 0 5px, ${T.blueTint} 5px 10px)` },
  ];
  return (
    <div style={{ paddingTop: reference ? 26 : 6 }}>
      <div style={{ position: 'relative' }}>
        {/* The marker sits on the same scale as the bar; its label hangs above so
            it never collides with the segments. */}
        {reference && (
          <div aria-hidden style={{
            position: 'absolute', insetInlineStart: w(reference.value), top: -22, bottom: -4,
            borderInlineStart: `2px solid ${T.ink}`,
          }}>
            <span style={{
              position: 'absolute', top: -2, insetInlineEnd: 6, whiteSpace: 'nowrap',
              fontSize: 11, color: T.ink, fontWeight: 700, ...tnum,
            }}>
              {reference.label} · {ils0(reference.value)}
            </span>
          </div>
        )}
        <div
          role="img"
          aria-label={`בוצע ${ils0(revenue)}, קבוע ביומן ${ils0(booked)}, תחזית ${ils0(forecast)}`}
          style={{
            display: 'flex', height: 30, borderRadius: 8, overflow: 'hidden',
            background: T.borderSoft,
          }}
        >
          {segs.map((sg) => (
            <div key={sg.label} title={`${sg.label} · ${ils0(sg.v)}`}
                 style={{ width: w(sg.v), background: sg.bg, flexShrink: 0 }} />
          ))}
        </div>
      </div>
      <Legend items={[
        { label: `בוצע · ${ils0(revenue)}`, color: T.blue },
        { label: `קבוע ביומן · ${ils0(booked - revenue)}`, color: '#93B4F7' },
        { label: `עוד לא נקבע · ${ils0(forecast - booked)}`, color: '#DCE7FB' },
      ]} />
      <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 6 }}>
        תחזית לסוף החודש: {ils0(forecast)}
        {reference && forecast > 0 && (
          <> · {pct2(Math.abs((forecast - reference.value) / reference.value) * 100)}{' '}
            {forecast >= reference.value ? 'מעל' : 'מתחת'} {reference.label}</>
        )}
      </div>
    </div>
  );
}

/* ── revenue against cost, month by month ─────────────────────────────────────
   Two series, one scale. Never a second y-axis: that is how two unrelated shapes
   get read as a relationship. */
export function TrendBars({ trend, revenue, cost }: {
  trend: { month: string; revenue: number; therapist_cost: number }[];
  revenue: number; cost: number;
}) {
  const max = Math.max(...trend.map((t) => Math.max(t.revenue, t.therapist_cost)), 1);

  /* 🔴 Bar heights are PIXELS, not percentages.
     The columns sit in a row with `align-items: flex-end`, which means they are NOT
     stretched to the row's height — so a child asking for `height: 60%` resolves
     against an auto-height parent, gets treated as `auto`, and collapses to nothing.
     That is exactly what happened: axis, gridlines and month labels drew, and the
     bars were invisible. Measuring against a fixed plot height removes the whole
     percentage chain. */
  const PLOT = 170;
  const barHeight = (v: number) => Math.max((v / max) * PLOT, 2);

  /* 🔴 `dir="ltr"` on a chart inside an RTL screen, deliberately. A time axis runs
     oldest-to-newest left-to-right — that is how the approved mockup draws it, and
     it is how the forecast chart beside it already drew it. Inheriting RTL put the
     newest month on the LEFT, so two charts in the same row flowed in opposite
     directions and the eye had to re-learn the axis between them. The Hebrew month
     labels under the bars still render as Hebrew; only the bar ORDER is pinned. */
  return (
    <div dir="ltr" style={{ paddingInlineStart: 46 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: PLOT, position: 'relative' }}>
        <ValueAxis max={max} height={PLOT} />
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <div key={g} aria-hidden style={{
            position: 'absolute', insetInline: 0, bottom: g * PLOT,
            borderTop: `1px solid ${T.borderSoft}`, pointerEvents: 'none',
          }} />
        ))}
        {trend.map((t, i) => {
          const last = i === trend.length - 1;
          return (
            <div key={t.month} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              justifyContent: 'flex-end', minWidth: 0, zIndex: 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
                <div title={`הכנסות ${ils(t.revenue)}`} style={{
                  flex: 1, background: T.blue, borderRadius: '4px 4px 0 0',
                  height: barHeight(t.revenue), opacity: last ? 1 : 0.82,
                }} />
                <div title={`עלות מטפלים ${ils(t.therapist_cost)}`} style={{
                  flex: 1, background: '#8B5CF6', borderRadius: '4px 4px 0 0',
                  height: barHeight(t.therapist_cost), opacity: last ? 1 : 0.82,
                }} />
              </div>
            </div>
          );
        })}
      </div>
      {/* The month labels sit OUTSIDE the plot box, in their own row, so the plot's
          height is the plot's height and nothing else competes for it. Only the
          current month is emphasised — a number on every bar is noise, and the
          point of the row is the shape. */}
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        {trend.map((t, i) => (
          <div key={t.month} style={{
            flex: 1, minWidth: 0, fontSize: 10, textAlign: 'center',
            color: i === trend.length - 1 ? T.ink : T.inkSoft,
            fontWeight: i === trend.length - 1 ? 700 : 400,
          }}>
            {t.month.slice(5)}
          </div>
        ))}
      </div>
      <div dir="rtl">
        <Legend items={[
          { label: `הכנסות · ${ils0(revenue)}`, color: T.blue },
          { label: `עלות מטפלים · ${ils0(cost)}`, color: '#8B5CF6' },
        ]} />
      </div>
    </div>
  );
}

/* ── the gross-profit bridge ──────────────────────────────────────────────────
   A waterfall and not three separate bars: the middle column is drawn as the SLICE
   it takes out of the first, so the eye reads subtraction rather than comparison.
   Avi's mockup has four bars — revenue, therapist cost, other direct costs, EBITDA.
   The third is out of the screen until the CFO gives a monthly figure, so there are
   three: the shape still tells the story and no bar stands for a number nobody
   holds. */
export function Bridge({ revenue, revenueLabel = 'הכנסות', cost, gross, pct }: {
  revenue: number; revenueLabel?: string; cost: number; gross: number; pct: number;
}) {
  const H = 190;
  const scale = (v: number) => (revenue > 0 ? Math.max((Math.abs(v) / revenue) * H, 2) : 2);
  const bars = [
    { label: revenueLabel, value: revenue, color: T.blue, from: 0 },
    { label: 'עלות מטפלים', value: -cost, color: '#8B5CF6', from: Math.max(revenue - cost, 0) },
    { label: 'רווח גולמי', value: gross, color: gross >= 0 ? T.green : T.red, from: 0 },
  ];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, height: H + 26, position: 'relative' }}>
        {/* 🔴 מה שהופך שלוש עמודות לגשר. בלי הקווים האלה העין רואה שלושה
            ברים מרחפים ולא מפל אחד, וזו בדיוק ההערה שחזרה על המסך.
            כל קו נמתח בין מרכזי שני ברים סמוכים בגובה המפלס המשותף להם:
            פסגת "עלות מטפלים" יושבת בדיוק על מפלס ההכנסות, ורצפתה בדיוק על
            מפלס הרווח הגולמי. insetInlineStart נמדד מימין ב-RTL, ולכן
            האחוזים כאן נכונים בלי להפוך דבר. */}
        {[{ at: 26, from: '16.666%' },
          { at: (H + 26) - (Math.max(gross, 0) / Math.max(revenue, 1)) * H, from: '50%' }].map((c) => (
          <div key={c.from} aria-hidden style={{
            position: 'absolute', insetInlineStart: c.from, width: '33.333%',
            top: c.at, borderTop: `1px dashed ${T.border}`, zIndex: 0,
          }} />
        ))}
        {bars.map((b) => (
          <div key={b.label} style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            justifyContent: 'flex-end', alignItems: 'center', height: '100%',
            position: 'relative', zIndex: 1,
          }}>
            <div style={{ ...tnum, fontSize: 12.5, fontWeight: 700, color: T.ink, marginBottom: 6 }}>
              {b.value < 0 ? `(${ils0(Math.abs(b.value))})` : ils0(b.value)}
            </div>
            <div
              title={`${b.label} ${ils(Math.abs(b.value))}`}
              style={{
                width: '100%', maxWidth: 104, height: scale(b.value),
                marginBottom: (b.from / Math.max(revenue, 1)) * H,
                background: b.color, borderRadius: '6px 6px 0 0',
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 24, marginTop: 9 }}>
        {bars.map((b) => (
          <div key={b.label} style={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: T.inkSoft }}>
            {b.label}
          </div>
        ))}
      </div>
      <div style={{ ...tnum, marginTop: 12, fontSize: 11.5, color: T.inkSoft, textAlign: 'center' }}>
        שיעור רווח גולמי {pct2(pct)} · עלויות המשרד והספקים טרם נכללות
      </div>
    </div>
  );
}
